<template>
  <div>
    <TreeSelection
      :docs-link="'toolguide/140-extracts.html'"
      :total-selected="totalSelected"
      :selections="selections"
      :options="validOptions"
      @on-add-selection="addSelection"
      @on-remove-selection="removeSelection"
      @on-change-selections="changeSelections"
    />
  </div>
</template>

<script>
import { computed, ref, watch } from 'vue';
import { size, get, reduce, map, groupBy, some, each, has } from 'lodash';
import { useRoute, useRouter } from 'vue-router';
import { useWorkpackagesStore } from '@/stores/workpackages';
import { useCompoundExtractStore } from '@/stores/compound-extract';
import { useWorkpackageScenarioSelectStore } from '@/stores/workpackage-scenario-select';
import { useBundlesStore } from '@/stores/bundles';
import { useInternationalization } from '@/composables/use-internationalization';
import { useFeature } from '@/composables/use-feature';
import { useCompoundExtract } from '@/composables/use-compound-extract';

export default {
  emits: ['update:scenarios'],
  setup() {
    const workpackagesStore = useWorkpackagesStore();
    const compoundExtractStore = useCompoundExtractStore();
    const bundlesStore = useBundlesStore();
    const { t } = useInternationalization('compoundExtractsPage.table');
    const { bundlesEnabled } = useFeature();
    const route = useRoute();
    const router = useRouter();
    const workpackageScenarioSelectStore = useWorkpackageScenarioSelectStore();
    const { isSpaceTest } = useCompoundExtract();

    const noBundle = { _id: 0, name: t('reportingPage.noBundle') };

    const selections = ref([]);
    const totalSelected = ref(0);
    const bundleSelections = ref(new Set());
    const workpackageSelections = ref(new Set());

    const validWorkpackages = computed(() => {
      // treeselect id cannot be null so use 0 instead
      return map(workpackagesStore.workpackages, w => ({
        ...w,
        id: w._id,
        label: w.name,
        bundleId: w.bundleId || 0,
      }));
    });

    const validWorkpackagesByBundleId = computed(() => {
      return groupBy(validWorkpackages.value, 'bundleId');
    });

    const loadData = async () => {
      totalSelected.value = 0;
      selections.value = [];
      const currentSelections = new Set();
      if (bundlesEnabled) {
        // Populate bundle selections on load
        each(workpackageScenarioSelectStore.selectedBundles, b => {
          const bundleId = b._id || 0;
          bundleSelections.value.add(bundleId);
          currentSelections.add(bundleId);
          each(validWorkpackagesByBundleId.value[bundleId], wp => {
            currentSelections.add(wp._id);
          });
          totalSelected.value += size(validWorkpackagesByBundleId.value[bundleId]);
        });
      }
      // Populate workpackage selections
      each(workpackageScenarioSelectStore.selectedWorkpackages, wp => {
        workpackageSelections.value.add(wp._id);
        currentSelections.add(wp._id);
      });
      selections.value = Array.from(currentSelections);
      totalSelected.value += size(workpackageScenarioSelectStore.selectedWorkpackages);
    };

    watch(
      () => workpackageScenarioSelectStore.selectedBundles,
      () => {
        loadData();
      },
      { deep: true, immediate: true }
    );

    return {
      workpackagesStore,
      compoundExtractStore,
      bundlesStore,
      workpackageScenarioSelectStore,
      noBundle,
      bundlesEnabled,
      selections,
      totalSelected,
      bundleSelections,
      workpackageSelections,
      validWorkpackages,
      validWorkpackagesByBundleId,
      route,
      router,
      t,
      isSpaceTest,
    };
  },

  data() {
    return { truncateThreshold: 25 };
  },

  computed: {
    validOptions() {
      if (!this.bundlesEnabled) return this.validWorkpackages;
      return map(this.validBundles, b => ({
        ...b,
        children: [...get(this.validWorkpackagesByBundleId, b._id, [])],
      }));
    },

    validBundles() {
      return reduce(
        [...this.bundlesStore.bundles, this.noBundle],
        (acc, b) => {
          const workpackages = this.validWorkpackagesByBundleId[b._id];
          const hasValidWorkpackages = some(workpackages, w => !w.disabled);
          if (size(workpackages) && hasValidWorkpackages) {
            acc.push({
              ...b,
              id: b._id,
              label: b.name,
            });
          }
          return acc;
        },
        []
      );
    },
  },

  methods: {
    addSelection(selection) {
      // Only bundle options will have children
      if (this.bundlesEnabled && has(selection, 'children')) {
        // Adding bundle
        this.bundleSelections.add(selection._id);
        this.totalSelected += 1;
      } else {
        // Adding workpackage
        this.workpackageSelections.add(selection._id);
        this.totalSelected += 1;
      }
    },

    removeSelection(selection) {
      if (this.bundlesEnabled && has(selection, 'children')) {
        // Removing bundle
        this.bundleSelections.delete(selection._id);
        this.totalSelected -= 1;
        // Remove any related wpIds
        each(this.validWorkpackagesByBundleId[selection._id], wp => {
          this.workpackageSelections.delete(wp._id);
        });
      } else {
        this.workpackageSelections.delete(selection._id);
        this.totalSelected -= 1;
        if (this.bundlesEnabled && this.bundleSelections.has(selection.bundleId)) {
          // If the related bundle is selected, remove it
          this.bundleSelections.delete(selection.bundleId);
          // Add all workpackages excluding the workpackage being removed
          each(this.validWorkpackagesByBundleId[selection.bundleId], wp => {
            if (wp._id !== selection._id) this.workpackageSelections.add(wp._id);
          });
        }
      }
    },

    async changeSelections() {
      if (this.bundlesEnabled) {
        const bundles = map(Array.from(this.bundleSelections), id =>
          get(this.bundlesStore.bundlesById, id, { _id: null, name: this.noBundle.name })
        );
        this.workpackageScenarioSelectStore.setSelectedBundles(bundles);
      }
      const workpackages = map(Array.from(this.workpackageSelections), id =>
        get(this.workpackagesStore.workpackagesById, id)
      );
      this.workpackageScenarioSelectStore.setSelectedWorkpackages(workpackages);

      const subType = get(this.compoundExtractStore, 'selectedExtract.series.subType', null);
      const spaceTest = this.isSpaceTest(subType);

      // Fetch scenarios for the new workpackages
      await this.workpackageScenarioSelectStore.fetchWorkpackageScenarios({
        shouldGetFinalOnly: !spaceTest,
      });
      this.$emit('update:scenarios', this.workpackageScenarioSelectStore.scenarios);

      const query = {
        ...this.route.query,
        _: new Date().getTime(),
        workpackageIds: map(this.workpackageScenarioSelectStore.selectedWorkpackages, '_id').slice(
          0
        ),
      };

      if (this.bundlesEnabled)
        query.bundleIds = map(this.workpackageScenarioSelectStore.selectedBundles, '_id').slice(0);

      this.router.push({
        path: this.route.path,
        query,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@style/base/_variables.scss';

.span-text {
  font-size: 1.4rem;
  font-weight: 600;
  color: $assortment-text-colour;
}

.sidebar-toggle-button {
  width: 32px;
  height: 32px !important;
}

:deep(.vue-treeselect__menu) {
  width: 500px !important;
}

:deep(.w-70) {
  width: 500px !important;
}
</style>
